import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import { apiClient } from "../lib/apiClient";
import { useI18n } from "../i18n/I18nProvider";
import { errorMessage } from "../i18n/apiErrors";
import LanguageSwitcher from "../components/LanguageSwitcher";
import PageWrap from "../components/PageWrap";

// ---------------------------------------------------------------------------
// VerifyEmailPage — E-Mail-Bestätigung über den Link aus der Bestätigungs-E-Mail
// (Req 7.1). Öffentliche Route `/verify-email?token=<rohtoken>`.
//
// Zustände (MUI gemäß ui-design-mui):
//  - Laden:   Card + CircularProgress, während der Verify-Request läuft.
//  - Erfolg:  Alert (success) mit lokalisiertem Hinweis; danach navigate("/"),
//             wobei der Hinweis als location.state mitgegeben wird.
//  - Fehler:  Alert (error) via errorMessage(t, err) + „E-Mail erneut senden“
//             mit E-Mail-Feld; Resend ruft POST /api/auth/resend-verification
//             auf und zeigt anschließend eine generische Snackbar.
// ---------------------------------------------------------------------------

type Phase = "loading" | "success" | "error";

// ---------------------------------------------------------------------------
// Dedupe-/„Erfolg-gewinnt"-Koordination über den StrictMode-Doppel-Mount hinweg
// (Bugfix verify-email-strictmode-double-request).
//
// Problem: Unter React-18-StrictMode (Dev) läuft mount → unmount → remount. Ein
// komponenten-lokaler useRef-Guard wird beim Remount frisch angelegt und
// verhindert den zweiten Token-verbrauchenden Request daher nicht. Der erste
// Request löst das Single-Use-Token ein (200), der zweite trifft auf das bereits
// verbrauchte Token (400) und überschreibt den Erfolg.
//
// Lösung: Pro Token einen modul-globalen Eintrag führen, der den Erfolg
// festhält. Sobald ein Request für ein Token erfolgreich war, „gewinnt" der
// Erfolg: ein Remount zeigt sofort den Erfolgszustand und ein nachgelagerter
// 400 desselben Tokens wird ignoriert (Guard im catch). Der Eintrag überlebt
// damit den kurzen Unmount→Remount-Zyklus desselben Link-Öffnens.
//
// Lebensdauer/Isolation: Jeder Mount zählt sich am Eintrag mit (`refCount`),
// das Cleanup zählt wieder herunter. Fällt `refCount` auf 0, wird der Eintrag
// in einem Makrotask verworfen. Ein unmittelbarer StrictMode-Remount hebt den
// Zähler synchron wieder an und bricht das Verwerfen ab; ein echtes, finales
// Unmount (und damit auch getrennte Test-Renderings) räumt den Eintrag auf.
// ---------------------------------------------------------------------------
type VerifyRecord = { succeeded: boolean; refCount: number };
const verifyRecords = new Map<string, VerifyRecord>();

/**
 * Nur für Tests: verwirft den modul-globalen Dedupe-Zustand, damit unabhängige
 * Renderings mit demselben Token sich nicht gegenseitig beeinflussen. In der
 * Produktion nicht nötig (jede Link-Öffnung ist ein eigener Seitenaufruf).
 */
export function __resetVerifyCacheForTests(): void {
  verifyRecords.clear();
}

export default function VerifyEmailPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  // Initialzustand: Lief für dieses Token schon ein erfolgreicher Verify
  // (z. B. der erste Lauf eines StrictMode-Doppel-Mounts), startet ein Remount
  // direkt im Erfolgszustand statt erneut zu „laden".
  const [phase, setPhase] = useState<Phase>(() =>
    token.length > 0 && verifyRecords.get(token)?.succeeded
      ? "success"
      : "loading",
  );
  // Fehler-OBJEKT halten, damit errorMessage(t, err) bei Sprachwechsel neu
  // auflöst (Muster wie LandingPage, Req 12.6).
  const [error, setError] = useState<unknown>(null);

  // Resend-Formular (nur im Fehlerzustand sichtbar).
  const [email, setEmail] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  useEffect(() => {
    // Ohne Token kann nicht verifiziert werden → direkt Fehlerzustand mit
    // generischem Fehlertext (ApiError-freier Fall nutzt den Fallback). Kein
    // Request, kein modul-globaler Eintrag.
    if (token.length === 0) {
      setError(null);
      setPhase("error");
      return;
    }

    // Modul-globalen Eintrag für dieses Token holen/anlegen und diesen Mount
    // dort mitzählen (überlebt den StrictMode-Unmount→Remount).
    let record = verifyRecords.get(token);
    if (record === undefined) {
      record = { succeeded: false, refCount: 0 };
      verifyRecords.set(token, record);
    }
    record.refCount += 1;

    let cancelled = false;

    function handleSuccess() {
      // „Erfolg gewinnt": Erfolg einmal modul-global festhalten, damit ein
      // nachgelagerter 400 (zweiter StrictMode-Lauf) ihn nicht überschreibt.
      record!.succeeded = true;
      if (cancelled) return;
      setPhase("success");
      // Nach kurzer Erfolgs-Anzeige zu "/" mit lokalisiertem Hinweis.
      window.setTimeout(() => {
        navigate("/", {
          replace: true,
          state: { notice: t("verifyEmail.success") },
        });
      }, 1500);
    }

    async function verify() {
      try {
        await apiClient.post("/api/auth/verify-email", { token });
        handleSuccess();
      } catch (err) {
        if (cancelled) return;
        // Guard: War der Verify für dieses Token bereits erfolgreich (z. B. der
        // erste Lauf des StrictMode-Doppel-Mounts), darf dieser Fehler — der aus
        // dem bereits verbrauchten Token des eigenen zweiten Requests stammt —
        // den Erfolg NICHT überschreiben.
        if (record!.succeeded) return;
        setError(err);
        setPhase("error");
      }
    }

    void verify();

    return () => {
      cancelled = true;
      record!.refCount -= 1;
      // Eintrag erst verwerfen, wenn kein Mount mehr daran hängt. Der Microtask
      // feuert erst nach dem aktuellen, synchronen Stack: ein unmittelbarer
      // StrictMode-Remount (unmount+remount im selben Tick) hat seinen Zähler
      // dann bereits wieder angehoben und bricht das Verwerfen ab. Ein echtes,
      // finales Unmount (inkl. getrennter Test-Renderings) räumt den Eintrag auf.
      queueMicrotask(() => {
        const current = verifyRecords.get(token);
        if (current !== undefined && current.refCount <= 0) {
          verifyRecords.delete(token);
        }
      });
    };
    // Absichtlich nur beim Mount/Token-Wechsel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleResend(e: FormEvent) {
    e.preventDefault();
    setIsResending(true);
    try {
      // Anti-Enumeration: Backend antwortet immer 200; Erfolg/Fehler führt zur
      // identischen, generischen Rückmeldung.
      await apiClient.post("/api/auth/resend-verification", { email });
    } catch {
      // Bewusst verschluckt: Die Rückmeldung bleibt generisch (Snackbar).
    } finally {
      setIsResending(false);
      setSnackOpen(true);
    }
  }

  return (
    <PageWrap center>
      <Card elevation={0} sx={{ width: "100%", maxWidth: 400, p: 3 }}>
        {/* Sprach_Umschalter oben rechts (anonymer Pfad). */}
        <Stack direction="row" justifyContent="flex-end" mb={1}>
          <LanguageSwitcher />
        </Stack>

        <Typography variant="h6" component="h1" sx={{ mb: 2 }}>
          {t("verifyEmail.title")}
        </Typography>

        {/* Laden */}
        {phase === "loading" && (
          <Stack alignItems="center" gap={2} sx={{ py: 3 }}>
            <CircularProgress aria-label={t("verifyEmail.loading")} />
            <Typography color="text.secondary">
              {t("verifyEmail.loading")}
            </Typography>
          </Stack>
        )}

        {/* Erfolg */}
        {phase === "success" && (
          <Alert severity="success" role="status">
            {t("verifyEmail.success")}
          </Alert>
        )}

        {/* Fehler + Resend */}
        {phase === "error" && (
          <Stack gap={2}>
            <Alert severity="error" role="alert">
              {error !== null ? errorMessage(t, error) : t("verifyEmail.error")}
            </Alert>

            <Box component="form" onSubmit={handleResend} noValidate>
              <Stack gap={2}>
                <TextField
                  id="resend-email"
                  type="email"
                  label={t("verifyEmail.emailLabel")}
                  placeholder={t("verifyEmail.emailPlaceholder")}
                  autoComplete="email"
                  required
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isResending}
                />
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isResending}
                  aria-busy={isResending}
                  sx={{ minHeight: 48 }}
                >
                  {isResending
                    ? t("verifyEmail.resending")
                    : t("verifyEmail.resendButton")}
                </Button>
              </Stack>
            </Box>
          </Stack>
        )}
      </Card>

      {/* Generische Resend-Rückmeldung (Anti-Enumeration). */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackOpen(false)}
          sx={{ width: "100%" }}
        >
          {t("verifyEmail.resendSnackbar")}
        </Alert>
      </Snackbar>
    </PageWrap>
  );
}
